import { motion } from 'framer-motion';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { springPresets } from '@/lib/motion';

interface CreditScoreGaugeProps {
  score: number;
  label: string;
  bureau?: string;
}

export function CreditScoreGauge({ score, label, bureau }: CreditScoreGaugeProps) {
  const percentage = (score / 850) * 100;
  const rotation = (percentage / 100) * 180 - 90;
  
  const getScoreColor = (score: number) => {
    if (score >= 740) return 'text-primary';
    if (score >= 670) return 'text-accent';
    if (score >= 580) return 'text-yellow-600';
    return 'text-destructive';
  };

  return (
    <motion.div
      className="relative w-full max-w-[200px] mx-auto"
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={springPresets.gentle}
    >
      <div className="relative aspect-square">
        {/* Background arc */}
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-muted opacity-20"
            strokeDasharray="126 252"
          />
          {/* Animated score arc */}
          <motion.circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className={getScoreColor(score)}
            strokeDasharray="126 252"
            strokeLinecap="round"
            initial={{ strokeDashoffset: 126 }}
            whileInView={{ strokeDashoffset: 126 - (126 * percentage) / 100 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div
            className={`text-4xl font-bold font-mono ${getScoreColor(score)}`}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, ...springPresets.snappy }}
          >
            {score}
          </motion.div>
          <div className="text-xs text-muted-foreground mt-1">{label}</div>
          {bureau && <div className="text-xs font-medium mt-0.5">{bureau}</div>}
        </div>
      </div>
    </motion.div>
  );
}

interface BeforeAfterBarProps {
  before: number;
  after: number;
  label: string;
  maxScore?: number;
}

export function BeforeAfterBar({ before, after, label, maxScore = 850 }: BeforeAfterBarProps) {
  const beforePercent = (before / maxScore) * 100;
  const afterPercent = (after / maxScore) * 100;
  const improvement = after - before;

  return (
    <motion.div
      className="space-y-3"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={springPresets.gentle}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground font-mono">{before}</span>
          <ArrowRight className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold text-primary font-mono">{after}</span>
          <span className="text-xs text-primary font-semibold ml-2">+{improvement}</span>
        </div>
      </div>
      
      <div className="relative h-8 bg-muted rounded-full overflow-hidden">
        {/* Before bar */}
        <motion.div
          className="absolute left-0 top-0 h-full bg-muted-foreground/30 rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: `${beforePercent}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
        
        {/* After bar */}
        <motion.div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary to-accent rounded-full"
          initial={{ width: 0 }}
          whileInView={{ width: `${afterPercent}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
        />
        
        {/* Score labels inside bars */}
        <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-mono font-semibold">
          <span className="text-foreground/70">{before}</span>
          <span className="text-white">{after}</span>
        </div>
      </div>
    </motion.div>
  );
}

interface CreditScoreComparisonProps {
  beforeScores: { equifax: number; experian: number; transunion: number };
  afterScores: { equifax: number; experian: number; transunion: number };
  timeframe?: string;
}

export function CreditScoreComparison({ beforeScores, afterScores, timeframe = '6 months' }: CreditScoreComparisonProps) {
  const avgBefore = Math.round((beforeScores.equifax + beforeScores.experian + beforeScores.transunion) / 3);
  const avgAfter = Math.round((afterScores.equifax + afterScores.experian + afterScores.transunion) / 3);
  const totalImprovement = avgAfter - avgBefore;

  return (
    <Card className="p-6 space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold">Real Client Results</h3>
        <p className="text-muted-foreground">Credit score transformation in {timeframe}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Before */}
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-sm font-semibold text-muted-foreground mb-2">BEFORE</div>
            <div className="text-5xl font-bold font-mono text-destructive">{avgBefore}</div>
            <div className="text-xs text-muted-foreground mt-1">Average Score</div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Equifax</span>
              <span className="font-mono font-semibold">{beforeScores.equifax}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Experian</span>
              <span className="font-mono font-semibold">{beforeScores.experian}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TransUnion</span>
              <span className="font-mono font-semibold">{beforeScores.transunion}</span>
            </div>
          </div>
        </div>

        {/* After */}
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-sm font-semibold text-muted-foreground mb-2">AFTER</div>
            <div className="text-5xl font-bold font-mono text-primary">{avgAfter}</div>
            <div className="text-xs text-muted-foreground mt-1">Average Score</div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Equifax</span>
              <span className="font-mono font-semibold text-primary">{afterScores.equifax}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Experian</span>
              <span className="font-mono font-semibold text-primary">{afterScores.experian}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">TransUnion</span>
              <span className="font-mono font-semibold text-primary">{afterScores.transunion}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Improvement highlight */}
      <motion.div
        className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5, ...springPresets.bouncy }}
      >
        <div className="flex items-center justify-center gap-2 text-primary">
          <TrendingUp className="w-5 h-5" />
          <span className="text-2xl font-bold font-mono">+{totalImprovement}</span>
          <span className="text-sm font-semibold">POINTS</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Average improvement across all bureaus</p>
      </motion.div>
    </Card>
  );
}

interface CreditProgressChartProps {
  data: { month: string; score: number }[];
  title?: string;
}

export function CreditProgressChart({ data, title = 'Your Credit Journey' }: CreditProgressChartProps) {
  const maxScore = 850;
  const minScore = 300;
  const range = maxScore - minScore;

  return (
    <Card className="p-6 space-y-4">
      <h3 className="text-xl font-bold">{title}</h3>
      
      <div className="relative h-64">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-muted-foreground font-mono">
          <span>850</span>
          <span>700</span>
          <span>600</span>
          <span>500</span>
          <span>300</span>
        </div>

        {/* Chart area */}
        <div className="ml-12 h-full relative">
          {/* Grid lines */}
          <div className="absolute inset-0 flex flex-col justify-between">
            {[850, 700, 600, 500, 300].map((score) => (
              <div key={score} className="border-t border-muted" />
            ))}
          </div>

          {/* Line chart */}
          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
            <motion.polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
              points={data
                .map((point, i) => {
                  const x = (i / (data.length - 1)) * 100;
                  const y = 100 - ((point.score - minScore) / range) * 100;
                  return `${x},${y}`;
                })
                .join(' ')}
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
            
            {/* Data points */}
            {data.map((point, i) => {
              const x = (i / (data.length - 1)) * 100;
              const y = 100 - ((point.score - minScore) / range) * 100;
              return (
                <motion.circle
                  key={i}
                  cx={`${x}%`}
                  cy={`${y}%`}
                  r="4"
                  fill="currentColor"
                  className="text-primary"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, ...springPresets.bouncy }}
                />
              );
            })}
          </svg>

          {/* X-axis labels */}
          <div className="absolute -bottom-6 left-0 right-0 flex justify-between text-xs text-muted-foreground">
            {data.map((point, i) => (
              <span key={i}>{point.month}</span>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
